import { useEffect, useRef } from 'react';

/**
 * CosmicHeroField — decorative canvas particle field for the primary landing
 * hero ONLY. Deep black base, concentrated cyan/blue core, violet/magenta
 * circulation, restrained warm highlights, slow organized motion.
 *
 * Purely decorative brand artwork — not a live data/telemetry visualization.
 * Original canvas-drawn composition (no external image assets, no hotlinked
 * references, no runtime API/model calls).
 */

const PALETTE = [
  { rgb: '34,211,238', weight: 0.30 },  // cyan
  { rgb: '59,130,246', weight: 0.25 },  // electric blue
  { rgb: '139,92,246', weight: 0.24 },  // violet
  { rgb: '217,70,239', weight: 0.16 },  // magenta
  { rgb: '251,191,120', weight: 0.05 }, // restrained warm highlight
];

function pickColor(rand) {
  let acc = 0;
  for (const c of PALETTE) {
    acc += c.weight;
    if (rand <= acc) return c.rgb;
  }
  return PALETTE[0].rgb;
}

function buildParticles(count, seedRand) {
  const particles = [];
  for (let i = 0; i < count; i++) {
    const r1 = seedRand();
    const r2 = seedRand();
    const r3 = seedRand();
    particles.push({
      angle: r1 * Math.PI * 2,
      // radius as a fraction of the field's core radius — concentrated near
      // center, thinning outward (sqrt bias keeps density believable)
      radiusFrac: Math.sqrt(r2) * 0.98,
      speed: 0.02 + r3 * 0.05,          // slow circulation, rad/s-ish
      phase: r1 * Math.PI * 2,
      color: pickColor(seedRand()),
      alpha: 0.35 + r3 * 0.60,
      size: 0.8 + r2 * 2.1,
    });
  }
  return particles;
}

// Deterministic small PRNG so the composition is stable across re-renders.
function makeRand(seed) {
  let s = seed;
  return function () {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

export default function CosmicHeroField() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return; // canvas 2D context unavailable (e.g. jsdom/test env, unsupported browser) — CSS glow layer remains as fallback
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const rand = makeRand(20260921);
    const isMobile = () => window.innerWidth < 768;
    const particleCount = isMobile() ? 90 : 150;
    const particles = buildParticles(particleCount, rand);

    // 4 filament curves — subtle organized flow lines through the field.
    const filaments = Array.from({ length: 4 }, (_, i) => ({
      seedA: rand() * Math.PI * 2,
      seedB: rand() * Math.PI * 2,
      radiusFrac: 0.35 + i * 0.16,
      color: pickColor(rand()),
    }));

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let running = false;
    let rafId = null;
    let startTime = performance.now();

    function centerPoint() {
      // Desktop: concentrated toward center-right. Mobile: centered, lower.
      if (width < 768) {
        return { cx: width * 0.5, cy: height * 0.58, coreR: Math.min(width, height) * 0.62 };
      }
      return { cx: width * 0.68, cy: height * 0.48, coreR: Math.min(width, height) * 0.58 };
    }

    function resize() {
      const rect = wrap.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function draw(elapsedMs) {
      const { cx, cy, coreR } = centerPoint();
      const t = elapsedMs / 1000;

      ctx.clearRect(0, 0, width, height);

      // Central concentrated glow — a restrained, slow pulse.
      const pulse = 0.85 + Math.sin(t * 0.35) * 0.10;
      const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR * pulse);
      coreGrad.addColorStop(0, 'rgba(170,230,255,0.75)');
      coreGrad.addColorStop(0.30, 'rgba(90,160,255,0.42)');
      coreGrad.addColorStop(0.6, 'rgba(140,80,230,0.22)');
      coreGrad.addColorStop(0.85, 'rgba(210,80,220,0.08)');
      coreGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, coreR * pulse, 0, Math.PI * 2);
      ctx.fill();

      // Secondary violet/magenta glow, offset — adds color richness without a second hard center.
      const secX = cx - coreR * 0.28;
      const secY = cy + coreR * 0.22;
      const secPulse = 0.9 + Math.sin(t * 0.28 + 2.1) * 0.12;
      const secGrad = ctx.createRadialGradient(secX, secY, 0, secX, secY, coreR * 0.55 * secPulse);
      secGrad.addColorStop(0, 'rgba(200,90,240,0.24)');
      secGrad.addColorStop(0.5, 'rgba(139,92,246,0.13)');
      secGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = secGrad;
      ctx.beginPath();
      ctx.arc(secX, secY, coreR * 0.55 * secPulse, 0, Math.PI * 2);
      ctx.fill();

      // Filaments — faint curved information-flow lines.
      filaments.forEach((f, i) => {
        const wobble = Math.sin(t * 0.12 + f.seedA) * coreR * 0.06;
        const r = coreR * f.radiusFrac;
        const x1 = cx + Math.cos(f.seedA) * r;
        const y1 = cy + Math.sin(f.seedA) * r * 0.7;
        const x2 = cx + Math.cos(f.seedB) * r * 1.15;
        const y2 = cy + Math.sin(f.seedB) * r * 0.7;
        const cxp = cx + wobble;
        const cyp = cy - r * 0.25;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.quadraticCurveTo(cxp, cyp, x2, y2);
        ctx.strokeStyle = `rgba(${f.color},${0.10 + (i % 2) * 0.04})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      // Particles — slow circulation around the core.
      particles.forEach((p) => {
        const angle = p.angle + t * p.speed;
        const radialDrift = Math.sin(t * 0.08 + p.phase) * 0.04;
        const r = coreR * (p.radiusFrac + radialDrift);
        const x = cx + Math.cos(angle) * r;
        const y = cy + Math.sin(angle) * r * 0.72; // slight ellipse flatten
        const twinkle = 0.7 + Math.sin(t * 0.6 + p.phase) * 0.3;
        ctx.beginPath();
        ctx.fillStyle = `rgba(${p.color},${(p.alpha * twinkle).toFixed(3)})`;
        ctx.arc(x, y, p.size, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    function loop(now) {
      if (!running) return;
      draw(now - startTime);
      rafId = requestAnimationFrame(loop);
    }

    function start() {
      if (running) return;
      running = true;
      startTime = performance.now();
      rafId = requestAnimationFrame(loop);
    }
    function stop() {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
    }

    resize();
    draw(0); // paint one frame immediately (also serves as the static/reduced-motion frame)

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => { resize(); draw(performance.now() - startTime); });
      ro.observe(wrap);
    } else {
      window.addEventListener('resize', resize);
    }

    let io = null;
    if (!prefersReducedMotion && typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver((entries) => {
        const visible = entries[0]?.isIntersecting;
        if (visible && document.visibilityState === 'visible') start();
        else stop();
      }, { threshold: 0.05 });
      io.observe(wrap);
    } else if (!prefersReducedMotion) {
      start();
    }

    const onVisibility = () => {
      if (prefersReducedMotion) return;
      if (document.visibilityState === 'hidden') stop();
      else start();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', resize);
      if (io) io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <div ref={wrapRef} className="pub-hero-cosmic-layer" aria-hidden="true" data-testid="hero-cosmic-field">
      <div className="pub-hero-cosmic-fallback-glow" />
      <canvas ref={canvasRef} className="pub-hero-cosmic-canvas" />
    </div>
  );
}
